import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Button,
  CircularProgress,
  Chip,
  Container,
  Stack,
  useTheme,
  alpha,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Call as CallIcon,
  CallEnd as CallEndIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { socketService } from '../services/socket';
import { addMessage, setActiveChat, updateChatStatus } from '../store/slices/chatSlice';
import { ChatMessage, ChatRequest, AgentUser } from '../types';
import { format, isToday, isYesterday } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard } from '../components/ui/GlassCard';
import { PageHeader } from '../components/ui/PageHeader';
import UserSelector from '../components/UserSelector';

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  
  const user = useSelector((state: RootState) => state.auth.user);
  const activeChat = useSelector((state: RootState) => state.chat.activeChat);
  const messages = useSelector((state: RootState) => 
    activeChat ? state.chat.messages[activeChat] || [] : []
  );
  
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AgentUser | null>(null);
  const [chatRequests, setChatRequests] = useState<ChatRequest[]>([]);
  const [characterCount, setCharacterCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [isInChat, setIsInChat] = useState(false);

  useEffect(() => {
    // Load chat requests
    checkForChatRequests();

    // Set up socket listeners
    const socket = socketService.getSocket();
    if (!socket) return;

    socket.on('chatMessage', (data: ChatMessage) => {
      dispatch(addMessage({ chatId: data.chatId, message: data }));
    });

    socket.on('chatRequest', (request: ChatRequest) => {
      setChatRequests(prev => [...prev, request]);
    });

    return () => {
      socket.off('chatMessage');
      socket.off('chatRequest');
    };
  }, [dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };


  const checkForChatRequests = async () => {
    try {
      const socket = socketService.getSocket();
      socket?.emit('getChatRequests', {}, (response: any) => {
        if (response.success) {
          setChatRequests(response.requests || []);
        }
      });
    } catch (error) {
      console.error('Failed to load chat requests:', error);
    }
  };

  const initializeChat = async (user: AgentUser | null) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const socket = socketService.getSocket();
      socket?.emit('initChat', { 
        RecipientId: user.Id,
        SessionId: user?.SessionId 
      }, (response: any) => {
        if (response.ChatId) {
          dispatch(setActiveChat(response.ChatId));
          setSelectedUser(user);
          setIsInChat(true);
        }
      });
    } catch (error) {
      console.error('Failed to initialize chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !activeChat || sending) return;

    setSending(true);
    try {
      const socket = socketService.getSocket();
      socket?.emit('sendChatMessage', {
        ChatId: activeChat,
        Message: message,
        MessageType: 'text',
        SessionId: user?.SessionId,
      }, (response: any) => {
        if (response.result) {
          const newMessage: ChatMessage = {
            id: response.MessageId,
            chatId: activeChat,
            content: message,
            senderId: user?.id || user?.Id || '',
            senderName: user?.name || user?.Name || 'You',
            timestamp: response.Timestamp || new Date().toISOString(),
            status: 'sent',
            encrypted: false,
          };
          dispatch(addMessage({ chatId: activeChat, message: newMessage }));
          setMessage('');
          setCharacterCount(0);
        }
      });
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleAcceptChatRequest = async (requestId: string) => {
    try {
      const socket = socketService.getSocket();
      socket?.emit('takeChatRequest', {
        RequestId: requestId,
        SessionId: user?.SessionId,
      }, (response: any) => {
        if (response.result && response.ChatId) {
          dispatch(setActiveChat(response.ChatId));
          setChatRequests(prev => prev.filter(r => (r.Id || r.id) !== requestId));
        }
      });
    } catch (error) {
      console.error('Failed to accept chat request:', error);
    }
  };

  const handleDeclineChatRequest = (requestId: string) => {
    setChatRequests(prev => prev.filter(r => r.Id !== requestId));
  };

  const handleEndChat = () => {
    setIsInChat(false);
    setSelectedUser(null);
    dispatch(setActiveChat(null));
  };

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    if (isToday(date)) return format(date, 'HH:mm');
    if (isYesterday(date)) return `Yesterday ${format(date, 'HH:mm')}`;
    return format(date, 'dd/MM/yyyy HH:mm');
  };


  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Chat Support"
        subtitle="Real-time messaging with customers and team members"
        breadcrumbs={[
          { label: 'Home', href: '/dashboard' },
          { label: 'Chat' },
        ]}
      />

      {/* Chat Requests Alert */}
      {chatRequests.length > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button 
                size="small" 
                startIcon={<CheckIcon />}
                onClick={() => handleAcceptChatRequest(chatRequests[0].Id || chatRequests[0].id)}
              >
                Accept
              </Button>
              <Button 
                size="small" 
                color="error"
                startIcon={<CloseIcon />}
                onClick={() => handleDeclineChatRequest(chatRequests[0].Id || chatRequests[0].id)}
              >
                Decline
              </Button>
            </Stack>
          }
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Incoming Chat Request
          </Typography>
          <Typography variant="body2">
            {chatRequests[0].Reason || chatRequests[0].reason || 'Customer is requesting assistance'}
          </Typography>
        </Alert>
      )}

      {/* User Selection Bar */}
      {!isInChat && (
        <GlassCard sx={{ mb: 3, p: 3 }}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Select Customer or Take Call
            </Typography>
            <UserSelector
              value={selectedUser}
              onChange={setSelectedUser}
              label="Select Customer to Chat With"
              placeholder="Search for customer by name, email, or company..."
              showCompanyFilter
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<CallIcon />}
                disabled={!selectedUser}
                onClick={() => selectedUser && initializeChat(selectedUser)}
                sx={{
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                    : theme.palette.primary.main,
                }}
              >
                Start Chat Session
              </Button>
            </Box>
          </Stack>
        </GlassCard>
      )}

      {/* Chat Area */}
      <GlassCard sx={{ display: 'flex', flexDirection: 'column', p: 0, height: isInChat ? 'calc(100vh - 300px)' : 'auto' }}>
        {isInChat && selectedUser ? (
            <>
            {/* Chat Header */}
            <Box sx={{ 
              p: 2, 
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              background: alpha(theme.palette.primary.main, 0.03),
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar>{selectedUser.fullName?.[0] || selectedUser.Name?.[0]}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedUser.fullName || selectedUser.Name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedUser.email || selectedUser.Email} • {selectedUser.agentName || selectedUser.Company}
                  </Typography>
                </Box>
                <Chip 
                  label={selectedUser.isOnline ? 'Online' : 'Offline'} 
                  size="small"
                  color={selectedUser.isOnline ? 'success' : 'default'}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<CallEndIcon />}
                  onClick={handleEndChat}
                >
                  End Chat
                </Button>
              </Box>
            </Box>


            {/* Message History */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto', 
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}>
                {messages.map((msg, index) => {
                  const isMine = msg.senderId === (user?.id || user?.Id) || msg.senderName === 'You';
                  return (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      style={{
                        display: 'flex',
                        justifyContent: isMine ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Paper
                        sx={{
                          p: 1.5,
                          px: 2,
                          maxWidth: '70%',
                          background: isMine
                            ? theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.main, 0.2)
                              : theme.palette.primary.main
                            : theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.paper, 0.6)
                              : theme.palette.grey[100],
                          color: isMine && theme.palette.mode === 'light' 
                            ? theme.palette.primary.contrastText 
                            : 'inherit',
                        }}
                      >
                        <Typography variant="body2">
                          {msg.content}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block', 
                            mt: 0.5, 
                            opacity: 0.7,
                            textAlign: isMine ? 'right' : 'left',
                          }}
                        >
                          {formatMessageTime(msg.timestamp.toString())}
                        </Typography>
                      </Paper>
                    </motion.div>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>

            {/* Message Input Area */}
            <Box sx={{ 
              p: 2, 
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              background: alpha(theme.palette.background.default, 0.5),
            }}>
                <Stack spacing={1}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Type your message..."
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      setCharacterCount(e.target.value.length);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={sending}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">
                      {characterCount} characters
                    </Typography>
                    <Button
                      variant="contained"
                      endIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                      onClick={handleSendMessage}
                      disabled={!message.trim() || sending}
                    >
                      Send
                    </Button>
                  </Box>
                </Stack>
              </Box>
          </>
        ) : (
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
            py: 8,
          }}>
            <PersonIcon sx={{ fontSize: 64, color: theme.palette.text.disabled }} />
            <Typography variant="h6" color="text.secondary">
              No Active Chat Session
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a customer above or wait for incoming chat requests
            </Typography>
          </Box>
        )}
      </GlassCard>
    </Container>
  );
};

export default Chat;